/**
 * Invoice API Endpoints
 * API calls for invoice operations
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import type { InvoicesResponse, InvoicesQueryParams } from '../types/invoice.types';
import type { InvoiceDetails } from '@/types/invoice';

// Use relative path in dev mode for Vite proxy
const isDevelopment = import.meta.env.DEV;
const COMMERCE_SERVICE_URL = isDevelopment
  ? '/commerce-service'
  : (import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service');

function getAuthToken(): string | null {
  return localStorage.getItem('saubhagya_jwt_token');
}

/**
 * Fetch invoices list with filters
 */
export async function getInvoices(params: InvoicesQueryParams): Promise<InvoicesResponse> {
  const queryParams = new URLSearchParams();

  // Pagination
  queryParams.set('page', String(params.page));
  queryParams.set('size', String(params.size));

  // Search
  if (params.searchTerm) {
    queryParams.set('searchTerm', params.searchTerm);
  }

  // Filters - Backend only supports single status (singular, not plural)
  if (params.statuses?.length) {
    // Backend expects 'status' (singular) and only supports one status at a time
    queryParams.set('status', params.statuses[0]);
    if (params.statuses.length > 1) {
      console.warn('[getInvoices] Backend only supports single status filter. Using first status:', params.statuses[0]);
    }
  }

  // Date range - Backend expects fromDate and toDate in ISO format
  if (params.dateFrom) {
    queryParams.set('fromDate', params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.set('toDate', params.dateTo);
  }

  // Sorting
  if (params.sortBy) {
    queryParams.set('sortBy', params.sortBy);
  }
  if (params.sortDirection) {
    queryParams.set('sortDirection', params.sortDirection);
  }

  // Sales unit filtering
  if (params.salesUnitIds && params.salesUnitIds.length > 0) {
    queryParams.set('salesUnitIds', params.salesUnitIds.join(','));
  }

  const token = getAuthToken();
  // CRITICAL FIX: Use /search endpoint instead of direct /invoices endpoint
  const apiUrl = `${COMMERCE_SERVICE_URL}/api/v1/invoices/search?${queryParams.toString()}`;
  console.log('[getInvoices] Fetching from:', apiUrl);

  const response = await fetch(apiUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch invoices');
  }

  const apiResponse = await response.json();

  // Map backend response structure to frontend expected structure
  const backendData = apiResponse.data || apiResponse;

  return {
    invoices: (backendData.content || []).map((invoice: any) => ({
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      status: invoice.status,
      customerName: invoice.buyerName,
      customerEmail: invoice.buyerEmail,
      totalAmount: invoice.totalAmount,
      amountPaid: invoice.paidAmount || 0,
      balanceDue: invoice.balanceAmount || (invoice.totalAmount - (invoice.paidAmount || 0)),
      notes: invoice.notes,
    })),
    totalElements: backendData.totalElements || 0,
    totalPages: backendData.totalPages || 0,
    currentPage: backendData.number || 0,
    pageSize: backendData.size || 20,
    hasNext: !backendData.last,
    hasPrevious: !backendData.first,
  };
}

/**
 * Fetch single invoice details
 */
export async function getInvoiceDetails(invoiceId: string): Promise<InvoiceDetails> {
  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/${invoiceId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch invoice details');
  }

  const data = await response.json();
  return data;
}

/**
 * Export invoices as CSV
 */
export async function exportInvoicesCSV(params: InvoicesQueryParams): Promise<Blob> {
  const queryParams = new URLSearchParams();

  // Add filters
  if (params.searchTerm) {
    queryParams.set('search', params.searchTerm);
  }
  // Backend only supports single status (singular, not plural)
  if (params.statuses?.length) {
    queryParams.set('status', params.statuses[0]);
    if (params.statuses.length > 1) {
      console.warn('[exportInvoicesCSV] Backend only supports single status filter. Using first status:', params.statuses[0]);
    }
  }
  if (params.dateFrom) {
    queryParams.set('fromDate', params.dateFrom);
  }
  if (params.dateTo) {
    queryParams.set('toDate', params.dateTo);
  }

  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/export/csv?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export invoices');
  }

  return response.blob();
}

/**
 * Export invoice as PDF
 */
export async function exportInvoicePDF(invoiceId: string): Promise<Blob> {
  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/${invoiceId}/export/pdf`,
    {
      method: 'GET',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export invoice as PDF');
  }

  return response.blob();
}

/**
 * Send invoice email
 */
export async function sendInvoiceEmail(
  invoiceId: string,
  email: string,
  ccEmails?: string[]
): Promise<{ success: boolean; message: string }> {
  const token = getAuthToken();
  const response = await fetch(
    `${COMMERCE_SERVICE_URL}/api/v1/invoices/${invoiceId}/send-email`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        recipientEmail: email,
        ccEmails: ccEmails || [],
        includeAttachment: true,
      }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to send invoice email');
  }

  const data = await response.json();
  return data;
}

export const invoiceApi = {
  getInvoices,
  getInvoiceDetails,
  exportInvoicesCSV,
  exportInvoicePDF,
  sendInvoiceEmail,
};

export default invoiceApi;
